import { defineField, defineType } from "sanity";

export const emailTemplate = defineType({
  name: "emailTemplate",
  title: "Email Template",
  type: "document",
  fields: [
    defineField({
      name: "name",
      title: "Template name",
      type: "string",
      description: "A name for this template (e.g. \"New Drops\", \"Sale Announcement\").",
      validation: (Rule) => Rule.required().error("Give the template a name.")
    }),
    defineField({
      name: "subject",
      title: "Subject line",
      type: "string",
      description: "The subject line recipients will see in their inbox.",
      validation: (Rule) => Rule.required().error("Add a subject line.")
    }),
    defineField({
      name: "previewText",
      title: "Preview text",
      type: "string",
      description: "Short preview/preheader text shown next to the subject in most email clients."
    }),
    defineField({
      name: "heading",
      title: "Heading",
      type: "string",
      description: "Main heading displayed in the email body."
    }),
    defineField({
      name: "body",
      title: "Body",
      type: "text",
      rows: 6,
      description: "Rich text email content (stored as HTML)."
    }),
    defineField({
      name: "ctaLabel",
      title: "CTA label",
      type: "string",
      description: "Text shown on the call-to-action button."
    }),
    defineField({
      name: "ctaUrl",
      title: "CTA link",
      type: "url",
      description: "Where the CTA button should link to.",
      validation: (Rule) =>
        Rule.uri({
          allowRelative: true,
          scheme: ["http", "https", "mailto"]
        })
    }),
    defineField({
      name: "includeProducts",
      title: "Include products",
      type: "boolean",
      description: "When enabled, a grid of latest products is added to the email.",
      initialValue: false
    }),
    defineField({
      name: "productSource",
      title: "Product Source",
      type: "string",
      description: "Where to pull products from when including a product grid.",
      options: {
        list: [
          { title: "New Arrivals", value: "new-arrivals" },
          { title: "Collection", value: "collection" },
          { title: "Category", value: "category" },
        ],
        layout: "dropdown",
      },
      initialValue: "new-arrivals",
      hidden: ({ parent }) => !parent?.includeProducts,
    }),
    defineField({
      name: "productSourceValue",
      title: "Collection or Category",
      type: "string",
      description: "The specific collection name or category to pull products from.",
      hidden: ({ parent }) => !parent?.includeProducts || parent?.productSource === "new-arrivals",
    }),
    defineField({
      name: "productCount",
      title: "Product count",
      type: "number",
      description: "How many products to show when the product grid is enabled.",
      validation: (Rule) => Rule.min(1).max(12),
      initialValue: 8
    })
  ],
  preview: {
    select: {
      title: "name",
      subtitle: "subject"
    }
  }
});
